/* eslint no-shadow: ["error", { "allow": ["state", "getters"] }] */
import Vue from 'vue';

import { MonitorState, MonitorStatus } from '@/config';
import { resolveRelations, loadBasedItems, loadItems } from '@/store/helpers';
import teamService from '@/services/team';
import monitorService from '@/services/monitor';

const handleStateSort = (sort) => {
    if (typeof sort !== 'undefined' && typeof sort.by !== 'undefined' && sort.by[0] === 'state') {
        return {
            by: ['status', 'state'],
            desc: [sort.desc[0], sort.desc[0]],
        };
    }
    return sort;
};

const state = {
    byId: {},
    allIds: [],
    listingIds: [],
    pendingIds: [],
    downIds: [],
    upIds: [],
    groups: {},
};

const getters = {
    // Return a single article with the given id.
    find: (state, _, __, rootGetters) => id =>
        // Swap ID referenes with the resolved author objects.
        resolveRelations(state.byId[id], ['incident', 'latency', 'availability', 'check', 'alertRule'], rootGetters),
    // Return a list of monitors in the order of `allIds`.
    list: (state, getters) => state.allIds.map(id => getters.find(id)),
    listing: (state, getters) => state.listingIds.map(id => getters.find(id)),
    pending: (state, getters) => state.pendingIds.map(id => getters.find(id)),
    down: (state, getters) => state.downIds.map(id => getters.find(id)),
    up: (state, getters) => state.upIds.map(id => getters.find(id)),
};

const actions = {
    loadById: async ({ commit }, monitorId) => {
        console.log('loadById action with monitorId', monitorId);
        const monitor = await monitorService.getById(monitorId, 'incident,availability,latency,check,alert-rule,location');
        // commit('clear'); // if we clear all the others we're going to have an empty list
        monitor.data = new Array(monitor.data); // convert data intro an array of one element
        loadItems(commit, monitor, ['incident', 'latency', 'availability', 'check', 'alertRule', 'location'], 'add');
        return monitor.meta;
    },
    loadByIdChecks: async ({ commit, state }, monitorId) => {
        console.log('loadById action with monitorId', monitorId);
        const monitor = await monitorService.getById(monitorId, 'check');
        monitor.data = new Array(monitor.data); // convert data intro an array of one element
        loadBasedItems(state.byId[monitorId], commit, monitor, ['check'], 'add');
        return monitor.meta;
    },
    clear: ({ commit }) => commit('clear'),
    load: async ({ commit, rootGetters }, param) => {
        const monitors = await teamService.getMonitors(rootGetters['team/id'], param.filter || {}, param.pagination || {}, handleStateSort(param.sort) || {});
        loadItems(commit, monitors, ['incident', 'latency', 'availability'], 'add');
        return monitors.meta;
    },
    loadListing: async ({ commit, rootGetters }, param) => {
        const monitors = await teamService.getMonitors(rootGetters['team/id'], param.filter || {}, param.pagination || {}, handleStateSort(param.sort) || {});
        loadItems(commit, monitors, ['incident', 'latency', 'availability'], 'addListing');
        return monitors.meta;
    },
    clearListing: ({ commit }) => commit('clearListing'),
    loadPending: async ({ commit, rootGetters }) => {
        const monitors = await teamService.getMonitors(
            rootGetters['team/id'],
            { state: MonitorState.UNKNOWN, status: MonitorStatus.ENABLED },
        );
        loadItems(commit, monitors, ['incident'], 'addPending');
    },
    loadDown: async ({ commit, rootGetters }) => {
        const monitors = await teamService.getMonitors(
            rootGetters['team/id'],
            { state: MonitorState.DOWN, status: MonitorStatus.ENABLED },
        );
        loadItems(commit, monitors, ['incident'], 'addDown');
    },
    loadUp: async ({ commit, rootGetters }) => {
        const monitors = await teamService.getMonitors(
            rootGetters['team/id'],
            { state: MonitorState.UP, status: MonitorStatus.ENABLED },
        );
        loadItems(commit, monitors, ['incident'], 'addUp');
    },
    // Persisting
    pause: async ({ dispatch }, id) => monitorService.pause(id)
        .then(() => dispatch('loadById', id)),
    resume: async ({ dispatch }, id) => monitorService.resume(id)
        .then(() => dispatch('loadById', id)),
};

const mutations = {
    clear: (state) => {
        state.allIds = [];
    },
    add: (state, item) => {
        Vue.set(state.byId, item.id, item);
        if (state.allIds.includes(item.id)) return;
        state.allIds.push(item.id);
    },
    addListing: (state, item) => {
        Vue.set(state.byId, item.id, item);
        if (state.listingIds.includes(item.id)) return;
        state.listingIds.push(item.id);
    },
    clearListing: (state) => {
        state.listingIds = [];
    },
    addPending: (state, item) => {
        Vue.set(state.byId, item.id, item);
        if (state.pendingIds.includes(item.id)) return;
        state.pendingIds.push(item.id);
    },
    addDown: (state, item) => {
        Vue.set(state.byId, item.id, item);
        if (state.downIds.includes(item.id)) return;
        state.downIds.push(item.id);
    },
    addUp: (state, item) => {
        Vue.set(state.byId, item.id, item);
        if (state.upIds.includes(item.id)) return;
        state.upIds.push(item.id);
    },
};

export default {
    actions,
    getters,
    mutations,
    namespaced: true,
    state,
};
